import { useTranslation } from 'react-i18next';
import {
  BasePreset,
  PRESET_TYPE_BOTTLE_SERVICE,
  PRESET_TYPE_GUEST_LIST,
  PRESET_TYPE_TICKET,
  PresetsRequest,
  PresetType,
} from '@domain/preset';
import { useGenericMutation } from '@hooks/use-generic-mutation';
import { useToast } from '@hooks/use-toast/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { request } from '@utils/request/request';
import { RequestMethod } from '@utils/request/request.type';

const deleteOffer = async (presetId: string, type: PresetType) => {
  const urlType = {
    [PRESET_TYPE_GUEST_LIST]: 'guest-list-presets',
    [PRESET_TYPE_BOTTLE_SERVICE]: 'bottle-service-presets',
    [PRESET_TYPE_TICKET]: 'ticket-presets',
  };

  const url = `${import.meta.env.VITE_OFFERS_HOST}/v1/nightgraph/${urlType[type]}/${presetId}`;

  return request({ url, method: RequestMethod.DELETE });
};

export const useDeleteAdmissionPreset = ({ floorplanId = '' }: { floorplanId?: string }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  return useGenericMutation(
    ['delete-admission-preset'],
    async ({ presetId, type }: { presetId: string; type: PresetType }) => deleteOffer(presetId, type),
    {
      onSuccess: (data: BasePreset, variables) => {
        const admissionsQueryKey = ['get-admissions-presets'];
        const floorplanAdmissionsQueryKey = ['get-floorplan-preset-admissions', floorplanId];

        const previousPresets = queryClient.getQueryData<PresetsRequest>(admissionsQueryKey);

        const previousFloorplanPresetAdmissions = queryClient.getQueryData<PresetsRequest>(floorplanAdmissionsQueryKey);

        if (previousPresets) {
          const presetsByType = previousPresets[variables.type];

          queryClient.setQueryData(admissionsQueryKey, {
            ...previousPresets,
            [variables.type]: presetsByType.filter((preset) => preset.id !== variables.presetId),
          });
        }

        if (previousFloorplanPresetAdmissions) {
          const presetsByType = previousFloorplanPresetAdmissions[variables.type];

          queryClient.setQueryData(floorplanAdmissionsQueryKey, {
            ...previousFloorplanPresetAdmissions,
            [variables.type]: presetsByType.filter((preset) => preset.id !== variables.presetId),
          });
        }

        toast.push({
          title: t('offers:deletedOfferSuccessNotificationTitle'),
          text: '',
          type: 'success',
        });
      },
    }
  );
};
